// Shopify Ajax Cart integration (works against the real storefront or the demo/mock emulation).
//
// Deck groups live in hidden line-item properties: _deck_id, _deck_name.
// Rules:
//  - a deck operation only ever touches lines whose _deck_id matches; loose lines and other decks are never changed
//  - adds are batched (<= 50 lines per /cart/add.js call)
//  - quantities are capped at stock (revalidated with the engine, minus what other lines already hold)
//  - Out-of-stock policy on the store is DENY, so Shopify answers 422 if we overshoot; we then retry line by line
//
// Events: window 'mcc:cart' {detail: cart}; document 'cart:refresh' for other listeners. Woodstock's drawer and
// header bubble don't listen for any event, so after every change we re-render them ourselves (refreshTheme).

import { api } from './singles-hub--api.js?v=c7c57de095';
import { config } from './singles-hub--config.js?v=c7c57de095';
import { hubTouch, track } from './singles-hub--analytics.js?v=c7c57de095';

const R = () => config().routes;

const BATCH = 50;
// The Ajax Cart API wants the numeric variant id; accept Shopify gids from any source.
export const vnum = (v) => String(v == null ? '' : v).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
const PROP_ID = '_deck_id';
const PROP_NAME = '_deck_name';

let last = null;
const listeners = new Set();
// Seeded from the server-rendered cart (singles-hub-config.liquid: window.__mccCart = {{ cart | json }}), so the
// first stock-bearing calls don't wait on a /cart.js round trip: on a phone the hub's cards appeared ~600 ms
// after its data had arrived because every call awaited /cart.js (2026-10-10). main.js still refreshes it.
try { if (typeof window !== 'undefined' && window.__mccCart && Array.isArray(window.__mccCart.items)) last = norm(window.__mccCart); } catch (e) { /* fetch it instead */ }

function norm(c) {
  const items = (c && c.items ? c.items : []).map((it) => ({
    key: it.key,
    variant_id: vnum(it.variant_id || it.id),
    qty: it.quantity,
    price: (it.final_price != null ? it.final_price : it.price) / 100,
    line_price: (it.final_line_price != null ? it.final_line_price : it.line_price) / 100,
    title: it.product_title || it.title,
    variant_title: it.variant_title || '',
    image: it.featured_image ? it.featured_image.url : it.image,
    url: it.url,
    handle: it.handle,
    properties: it.properties || {},
    deck_id: (it.properties || {})[PROP_ID] || null,
    deck_name: (it.properties || {})[PROP_NAME] || null,
    card_id: (it.properties || {})._card_id || null,
  }));
  return {
    items,
    count: items.reduce((a, l) => a + l.qty, 0),
    total: (c && c.total_price != null ? c.total_price : items.reduce((a, l) => a + l.line_price * 100, 0)) / 100,
    raw: c,
  };
}

async function call(path, body) {
  const res = await fetch(path, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const err = new Error((data && (data.description || data.message)) || 'Cart request failed');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function emit(cart) {
  last = cart;
  listeners.forEach((fn) => { try { fn(cart); } catch (e) { console.error(e); } });
  try { window.dispatchEvent(new CustomEvent('mcc:cart', { detail: cart })); } catch (e) { /* ignore */ }
  try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: cart.raw })); } catch (e) { /* ignore */ }
}

// Re-render Woodstock's cart drawer contents + header bubble from the Section Rendering API (what its own product
// form does, minus opening the drawer). Without it a hub add only showed up after a page refresh (2026-10-16).
const THEME_PARTS = [['cart-drawer', '.cart-drawer__items'], ['cart-drawer', '.cart-drawer__footer--subtotal'], ['cart-icon-bubble', null]];
async function refreshTheme(c) {
  const drawer = document.querySelector('cart-drawer');
  const bubble = document.getElementById('cart-icon-bubble');
  if (!drawer && !bubble) return;
  try {
    const res = await fetch(`${location.pathname}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin' });
    if (!res.ok) return;
    const sections = await res.json();
    const empty = !(c && c.count);
    if (drawer) {
      drawer.classList.toggle('is-empty', empty);
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', empty);
    }
    THEME_PARTS.forEach(([id, sel]) => {
      if (!sections[id]) return;
      const doc = new DOMParser().parseFromString(sections[id], 'text/html');
      const from = sel ? doc.querySelector(sel) : doc.querySelector('.shopify-section');
      const to = sel ? drawer && drawer.querySelector(sel) : bubble;
      if (from && to) to.innerHTML = from.innerHTML;
    });
    if (drawer && typeof drawer.updateProductRecommendation === 'function') { try { drawer.updateProductRecommendation(); } catch (e) { /* ignore */ } }
    document.dispatchEvent(new CustomEvent('mcc:cart:rendered', { detail: c && c.raw }));
  } catch (e) { /* the drawer catches up on the next page load */ }
}

export const cart = {
  last: () => last,
  subscribe(fn) { listeners.add(fn); if (last) fn(last); return () => listeners.delete(fn); },

  // first paint: the server-seeded cart if there is one (no round trip), else /cart.js. main.js refreshes it
  // in the background and pages that show stock re-fetch through cart.subscribe when the basket differs.
  ready() { return last ? Promise.resolve(last) : cart.get(); },

  async get() {
    const c = norm(await call(R().cartJs));
    emit(c);
    return c;
  },

  // Deck groups for the basket drawer / builder diffs
  groups(c) { return groupLines(c || last || { items: [] }); },

  deckLines(c, deckId) { return (c || last || { items: [] }).items.filter((l) => l.deck_id === deckId); },

  // How many of a variant are held by lines OTHER than the given deck (for stock reservation)
  heldElsewhere(c, deckId) {
    const out = {};
    (c || last || { items: [] }).items.forEach((l) => { if (l.deck_id !== deckId) out[l.variant_id] = (out[l.variant_id] || 0) + l.qty; });
    return out;
  },

  // Add loose (non-deck) items. items: [{variant_id, qty, properties?}]
  async addItems(items, { capStock = true } = {}) {
    const c0 = last || await cart.get();
    let want = items.filter((i) => i.qty > 0);
    const capped = [];
    if (capStock && want.length) {
      const capInfo = await capToStock(want, c0, null);
      want = capInfo.items;
      capped.push(...capInfo.capped);
    }
    const res = await addBatched(want, 'add');
    if (res.added.length) track('hub_items_added', { lines: res.added.length, cards: res.added.reduce((a, x) => a + x.qty, 0) });
    const c = await cart.get();
    if (res.added.length) refreshTheme(c);
    return Object.assign(res, { capped, cart: c });
  },

  // Make the deck group contain exactly `lines` ([{variant_id, qty}]). Other lines untouched.
  async syncDeck(deckId, deckName, lines, { capStock = true } = {}) {
    const c0 = await cart.get();
    const mine = c0.items.filter((l) => l.deck_id === deckId);
    const have = {};
    mine.forEach((l) => { have[l.variant_id] = (have[l.variant_id] || 0) + l.qty; });
    const target = {};
    lines.forEach((l) => { if (l.qty > 0) target[vnum(l.variant_id)] = (target[vnum(l.variant_id)] || 0) + l.qty; });
    // decreases/removals via update.js keyed by line key
    const updates = {};
    mine.forEach((l) => {
      const t = target[l.variant_id] || 0;
      if (t < have[l.variant_id]) {
        // reduce this line (merge duplicates down to one line)
        const keep = Math.max(0, t - (updates.__kept && updates.__kept[l.variant_id] || 0));
        updates[l.key] = Math.min(l.qty, keep);
        updates.__kept = updates.__kept || {};
        updates.__kept[l.variant_id] = (updates.__kept[l.variant_id] || 0) + updates[l.key];
      }
    });
    delete updates.__kept;
    let removed = 0;
    if (Object.keys(updates).length) {
      mine.forEach((l) => { if (updates[l.key] !== undefined) removed += l.qty - updates[l.key]; });
      await updateLines(updates);
    }
    // increases via add.js with deck properties
    let adds = [];
    Object.keys(target).forEach((v) => {
      const d = target[v] - (have[v] || 0);
      if (d > 0) {
        const extra = (lines.find((l) => vnum(l.variant_id) === v) || {}).properties || {};
        adds.push({ variant_id: v, qty: d, properties: Object.assign({}, extra, { [PROP_ID]: deckId, [PROP_NAME]: deckName }) });
      }
    });
    const capped = [];
    if (capStock && adds.length) {
      const c1 = Object.keys(updates).length ? await cart.get() : c0;
      const capInfo = await capToStock(adds, c1, null);
      adds = capInfo.items;
      capped.push(...capInfo.capped);
    }
    const res = await addBatched(adds, 'deck_add');
    // keep the deck name current on existing lines is not possible without re-adding; the drawer reads the newest name
    const c = await cart.get();
    if (res.added.length || removed) refreshTheme(c);
    return Object.assign(res, { removed, capped, cart: c });
  },

  async removeDeck(deckId) {
    const c0 = await cart.get();
    const updates = {};
    c0.items.forEach((l) => { if (l.deck_id === deckId) updates[l.key] = 0; });
    if (Object.keys(updates).length) await updateLines(updates);
    const c = await cart.get();
    if (Object.keys(updates).length) refreshTheme(c);
    return c;
  },

  async changeLine(key, qty) {
    const c = norm(await call(R().cartChange, { id: key, quantity: qty }));
    emit(c);
    refreshTheme(c);
    return c;
  },
};

async function updateLines(updates) {
  try {
    const c = norm(await call(R().cartUpdate, { updates }));
    emit(c);
    return c;
  } catch (err) {
    // fall back to one change.js per line
    let c = null;
    for (const [key, qty] of Object.entries(updates)) c = norm(await call(R().cartChange, { id: key, quantity: qty }));
    if (c) emit(c);
    return c;
  }
}

async function addBatched(items, action) {
  const added = [], failed = [];
  for (let i = 0; i < items.length; i += BATCH) {
    const chunk = items.slice(i, i + BATCH);
    const payload = { items: chunk.map((x) => ({ id: Number(vnum(x.variant_id)) || vnum(x.variant_id), quantity: x.qty, properties: x.properties || {} })) };
    try {
      await call(R().cartAdd, payload);
      added.push(...chunk);
    } catch (err) {
      if (err.status !== 422 || chunk.length === 1) { chunk.forEach((x) => failed.push(Object.assign({ reason: err.message }, x))); continue; }
      // one bad line fails the whole request on Shopify: retry line by line
      for (const x of chunk) {
        try { await call(R().cartAdd, { items: [{ id: Number(vnum(x.variant_id)) || vnum(x.variant_id), quantity: x.qty, properties: x.properties || {} }] }); added.push(x); }
        catch (e2) { failed.push(Object.assign({ reason: e2.message }, x)); }
      }
    }
  }
  // credit the order to this hub page (cart attributes -> order note_attributes); the caller re-reads the cart after
  if (added.length) await hubTouch(action || 'add', last && last.raw ? last.raw.attributes : null);
  return { added, failed };
}

// Ask the engine what is really available, subtract what other cart lines hold, cap.
async function capToStock(items, c, deckId) {
  const held = {};
  c.items.forEach((l) => { held[l.variant_id] = (held[l.variant_id] || 0) + l.qty; });
  let avail = null;
  try {
    const r = await api.revalidate(items.map((i) => ({ variant_id: vnum(i.variant_id), qty: i.qty })));
    avail = {};
    (r.items || []).forEach((x) => { avail[vnum(x.variant_id)] = x; });
  } catch (e) {
    return { items, capped: [] }; // offline: let Shopify's DENY policy be the backstop
  }
  const out = [], capped = [];
  items.forEach((i) => {
    const a = avail[vnum(i.variant_id)];
    if (!a) { out.push(i); return; }
    const free = Math.max(0, (a.available || 0) - (held[vnum(i.variant_id)] || 0));
    const q = Math.min(i.qty, free);
    if (q < i.qty) capped.push({ variant_id: i.variant_id, wanted: i.qty, got: q, price_changed: !!a.changed, price: a.price });
    if (q > 0) out.push(Object.assign({}, i, { qty: q }));
  });
  return { items: out, capped };
}

// ------------------------------------------------ S11 basket grouping helper (pure)
// Returns {decks:[{id,name,lines,count,total}], loose:[...], conflicts:[]}.
// conflicts need stock data; see basket.js which enriches them.
export function groupLines(c) {
  const decks = new Map();
  const loose = [];
  (c.items || []).forEach((l) => {
    if (!l.deck_id) { loose.push(l); return; }
    if (!decks.has(l.deck_id)) decks.set(l.deck_id, { id: l.deck_id, name: l.deck_name || 'Deck', lines: [], count: 0, total: 0 });
    const d = decks.get(l.deck_id);
    d.lines.push(l);
    d.count += l.qty;
    d.total += l.line_price;
    if (l.deck_name) d.name = l.deck_name;
  });
  return { decks: [...decks.values()], loose };
}

// Open the basket: demo shell / theme hook first (MCC_CONFIG.openCart), then Woodstock's drawer element, then /cart.
export function openBasket() {
  const c = window.MCC_CONFIG || {};
  if (typeof c.openCart === 'function') { c.openCart(); return; }
  const d = document.querySelector('cart-drawer');
  if (d && typeof d.open === 'function') { d.open(); return; }
  location.assign(config().routes.cart || '/cart');
}
